import { useEffect, useState } from "react";
import { apiPcf, type AntecipadoLinha, type TrilhaPedido } from "../lib/api";

// Faturamento antecipado (reunião 24/10/2026): pedidos faturados ANTES de produzir cuja PCF ainda não foi
// programada na produção. Fica no topo do Planejamento e da Fila — some quando todas as PCFs do pedido forem
// enviadas à produção (Finalizar/Confirmação), entrarem no controle do chão de fábrica ou forem finalizadas.

const COR = "#c2410c";

function prazo(d: number | null) {
  if (d == null) return "";
  if (d < 0) return `${-d} dia(s) atrasado`;
  if (d === 0) return "hoje";
  return `${d} dia(s)`;
}

export function AntecipadosAlerta({ onAbrirPcf }: { onAbrirPcf?: (numpcf: number) => void }) {
  const [itens, setItens] = useState<AntecipadoLinha[]>([]);
  const [erpUrl, setErpUrl] = useState("");
  const [aberto, setAberto] = useState(false);
  const [trilha, setTrilha] = useState<TrilhaPedido | null>(null);

  useEffect(() => {
    apiPcf
      .antecipados()
      .then((r) => {
        setItens(r.itens ?? []);
        setErpUrl(r.erpUrl ?? "");
      })
      .catch(() => setItens([]));
  }, []);

  if (!itens.length) return null;

  const abrirPcf = (n: number) => {
    if (onAbrirPcf) onAbrirPcf(n);
    else window.open(`${window.location.pathname}#pcf=${n}`, "_blank", "noopener");
  };

  return (
    <div style={{ margin: "8px 16px", border: "1px solid #fdba74", background: "#fff7ed", borderRadius: 8, color: "#7c2d12" }}>
      <button
        className="btn-secundario"
        style={{ width: "100%", textAlign: "left", border: 0, background: "transparent", color: COR, fontWeight: 700 }}
        onClick={() => setAberto((v) => !v)}
        title="Pedidos faturados antecipadamente e ainda não enviados/programados na produção"
      >
        ⚠ {itens.length} pedido(s) faturado(s) antecipadamente e ainda NÃO programado(s) na produção {aberto ? "▲" : "▼"}
      </button>
      {aberto && (
        <div className="tabela-wrap" style={{ margin: 0, maxHeight: 320 }}>
          <table className="tabela-pcfs">
            <thead>
              <tr>
                <th className="num">Pedido</th>
                <th>Cliente</th>
                <th className="num">NF</th>
                <th>Faturado em</th>
                <th className="num">Dias desde fat.</th>
                <th>Entrega</th>
                <th>Prazo</th>
                <th>Programar (abrir a PCF)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {itens.map((l) => (
                <tr key={l.numped}>
                  <td className="num">
                    {erpUrl ? (
                      <a href={`${erpUrl}/?go=ATCADPED&numped=${l.numped}`} target="_blank" rel="noopener" title="Abrir o pedido no sistema">
                        {l.numped}
                      </a>
                    ) : (
                      l.numped
                    )}
                  </td>
                  <td>{l.nomcli}</td>
                  <td className="num">{l.numenf || ""}</td>
                  <td>{l.datfat}</td>
                  <td className="num">{l.diasFat ?? ""}</td>
                  <td>{l.datent}</td>
                  <td style={l.diasEntrega != null && l.diasEntrega <= 0 ? { color: "#c0392b", fontWeight: 700 } : undefined}>
                    {prazo(l.diasEntrega)}
                  </td>
                  <td>
                    {l.pcfs.map((f) => (
                      <button
                        key={f.numpcf}
                        className="btn-acao"
                        style={{ padding: "2px 8px", marginRight: 4 }}
                        title={`${f.nompro} — ${f.situac} (seq ${f.sequen})`}
                        onClick={() => abrirPcf(f.numpcf)}
                      >
                        PCF {f.numpcf}
                      </button>
                    ))}
                  </td>
                  <td>
                    <button
                      className="btn-secundario"
                      style={{ padding: "2px 8px" }}
                      onClick={() => apiPcf.trilha(l.numped).then(setTrilha).catch(() => setTrilha(null))}
                    >
                      Trilha
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {trilha && trilha.ok && (
        <div className="modal-backdrop" onClick={() => setTrilha(null)}>
          <div className="modal-panel" style={{ width: "min(1140px, 96vw)" }} onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <div>
                <div className="modal-eyebrow">Trilha do pedido{trilha.fatAntecipado ? " · faturamento antecipado" : ""}</div>
                <h2>
                  {trilha.numped} <small>— {trilha.situac}</small>
                </h2>
              </div>
              <button className="btn-secundario" onClick={() => setTrilha(null)}>
                Fechar
              </button>
            </header>
            <div style={{ display: "flex", padding: "18px 22px", overflowX: "auto" }}>
              {(trilha.etapas ?? []).map((e, i, arr) => (
                <div key={e.k} style={{ display: "flex", flex: i < arr.length - 1 ? "1 1 0" : "0 0 auto", minWidth: 0 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 96, textAlign: "center" }}>
                    <div
                      style={{
                        width: 26, height: 26, borderRadius: "50%", lineHeight: "26px", color: "#fff", fontWeight: 700,
                        background: e.feito ? (e.k === "cancelado" ? "#c0392b" : "#16a34a") : "#cbd5e1",
                      }}
                    >
                      {e.feito ? "✓" : i + 1}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4, opacity: e.feito ? 1 : 0.6 }}>{e.t}</div>
                    <div style={{ fontSize: 11, opacity: 0.7 }}>{e.data || " "}</div>
                    {e.info ? <div style={{ fontSize: 11, opacity: 0.7 }}>{e.info}</div> : null}
                  </div>
                  {i < arr.length - 1 && (
                    <div style={{ flex: 1, height: 3, marginTop: 12, background: arr[i + 1]?.feito ? "#16a34a" : "#cbd5e1" }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
